<template>
  <Modal
    v-model:visible="isVisible"
    class="add-ad-hoc-brokered-carrier-modal"
    :dismissable-mask="false"
    :header="t('common.addParam', { param: t('brokerages.brokeredCarrier') })"
    @keypress.enter="onSave"
  >
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      input-id="add-ad-hoc-brokered-carrier-modal-carrier-name"
      :errors="v$.name.$errors"
      :label="t('common.paramName', { param: t('carriers.carriers') })"
      class="field"
    >
      <InputText
        :id="inputId"
        v-model="carrier.name"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :placeholder="t('common.paramName', { param: t('carriers.carriers') })"
        autofocus
      />
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputId }"
      input-id="add-ad-hoc-brokered-carrier-modal-carrier-scac"
      :label="`${t('carriers.carriers')} ${t('carriers.scac')}`"
      class="field"
      optional
    >
      <InputText
        :id="inputId"
        v-model="carrier.scac"
        v-bind="ariaProps"
        :placeholder="`${t('carriers.carriers')} ${t('carriers.scac')}`"
      />
    </InputWrapper>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :label="t('common.save')"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { provide, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';
import { CarrierRef } from '@rygen/rygen-ui/model/carrier/CarrierRef';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const emit = defineEmits<{
  (event: 'ad-hoc-brokered-carrier-added', carrier: CarrierRef): void;
}>();

const { t } = useI18n();
const { uid } = useUid('adhoc-brokered-carrier-modal');
provide('scope', uid);

const carrier = ref(new CarrierRef());

const { v$, validate } = useValidations(
  carrier,
  {
    name: {
      required: required(
        t('common.paramName', { param: t('carriers.carriers') }),
      ),
    },
  },
  uid,
);

async function onSave() {
  if (await validate()) {
    isVisible.value = false;
    emit('ad-hoc-brokered-carrier-added', carrier.value);
  }
}

watch(isVisible, (newVal) => {
  if (newVal) {
    v$.value.$reset();
  }
});
</script>
